import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

// Tokens del proyecto
const BG   = 'var(--color-surface-low)';
const TEXT = 'var(--color-on-surface)';
const BLUE = '#4a7bc4';
const RED  = '#f87171';

const labelCls = 'block text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider mb-1.5';

/**
 * BasicDatePicker — popup de MUI con input estilizado al diseño del proyecto.
 *
 * Props:
 *   label    — label renderizado arriba del campo
 *   name     — name del campo
 *   value    — fecha en formato 'YYYY-MM-DD' o null
 *   onChange — callback con fecha en formato 'YYYY-MM-DD' o null
 *   error    — mensaje de error (string)
 *   hasError — bool para borde rojo (sin mensaje, ej. desde ClientForm)
 *   width    — ancho del campo (default '100%')
 *   clearable — bool, muestra una "X" para vaciar el campo (default false)
 *   disabled  — campo bloqueado (no editable): se ve grisado y con cursor "no permitido",
 *               acorde al tema claro/oscuro
 */
export default function BasicDatePicker({ label, name, value, onChange, error, hasError, width, clearable = false, disabled = false }) {
  const handleDateChange = (newValue) => {
    const formatted = newValue ? dayjs.utc(newValue).format('YYYY-MM-DD') : null;
    onChange(formatted);
  };

  const isError = Boolean(error) || Boolean(hasError);
  const fieldBg = disabled ? 'var(--color-field-locked)' : BG;
  const fieldText = disabled ? 'var(--color-on-surface-muted)' : TEXT;

  return (
    <div className="w-full">
      {label && <label className={labelCls}>{label}</label>}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          name={name}
          value={value ? dayjs(value) : null}
          onChange={handleDateChange}
          format="DD/MM/YYYY"
          disabled={disabled}
          slotProps={{
            field: {
              clearable,
              onClear: () => onChange(null),
            },
            textField: {
              size: 'small',
              error: isError,
              helperText: error || '',
              sx: {
                width: width || '100%',
                '& .MuiInputLabel-root': { display: 'none' },
                '& .MuiInputBase-root': {
                  borderRadius: '8px',
                  backgroundColor: fieldBg,
                  border: isError
                    ? `1px solid ${RED}`
                    : disabled
                      ? '1px solid var(--color-field-locked-border)'
                      : '1px solid transparent',
                  fontSize: '0.875rem',
                  color: fieldText,
                  cursor: disabled ? 'not-allowed' : 'auto',
                  transition: 'border-color 150ms, box-shadow 150ms',
                  boxShadow: isError ? '0 0 0 2px rgba(248,113,113,0.12)' : 'none',
                  '&:hover': { backgroundColor: fieldBg },
                  '&.Mui-disabled': { backgroundColor: fieldBg },
                  '&.Mui-focused': {
                    borderColor: isError ? RED : 'rgba(74,123,196,0.4)',
                    boxShadow: isError
                      ? '0 0 0 2px rgba(248,113,113,0.12)'
                      : '0 0 0 2px rgba(74,123,196,0.10)',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiInputBase-input': {
                  padding: '8px 12px',
                  color: fieldText,
                  fontSize: '0.875rem',
                  WebkitTextFillColor: disabled ? 'var(--color-on-surface-muted)' : undefined,
                },
                '& .MuiInputAdornment-root .MuiIconButton-root': {
                  color: 'var(--color-on-surface-muted)',
                  '&:hover': { color: BLUE },
                },
                '& .MuiFormHelperText-root': {
                  marginLeft: 0,
                  marginTop: '4px',
                  fontSize: '0.75rem',
                  color: '#ef4444',
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </div>
  );
}
